import {
  Incident,
  IncidentComment,
  IncidentLinkedItem,
  PaginatedIncidentsResponse,
  IncidentKanbanLink,
  IncidentWorkspaceSummary,
  IncidentAssignee,
  IncidentWatcher,
  IncidentActivityLog,
  IncidentTimelineEntry,
  IncidentWithKanbanSummary,
  PaginatedIncidentsWithKanbanSummaryResponse,
  PromoteIncidentToCardRequest,
  AdoptCardAsIncidentRequest,
  CreateIncidentKanbanLinkRequest,
  UpdateIncidentKanbanLinkRequest,
  IncidentStatus,
} from '../types/incident';
import type { KanbanCard } from '../types/kanban';
import { Evidence } from '../types/player';

const INCIDENTS_API_BASE_URL = 'https://spy.playcdu.co/api/notes';

// ── Promote / Adopt response shapes (kept here to avoid an `Incident`↔`KanbanCard` cycle in `types/`) ──

export interface PromoteIncidentToCardResponse {
  card: KanbanCard;
  link: IncidentKanbanLink;
  created_card: boolean;
}

export interface AdoptCardAsIncidentResponse {
  incident: Incident;
  link: IncidentKanbanLink;
  created_incident: boolean;
}

export interface UpdateIncidentWithSyncRequest {
  title?: string;
  description?: string;
  status?: IncidentStatus;
  priority?: number;
}

class IncidentsApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${INCIDENTS_API_BASE_URL}${endpoint}`, {
      ...options,
      // Cloudflare Access handles authentication via cookies
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    return response.json();
  }

  async createIncident(data: {
    title: string;
    description: string;
    status?: string;
    priority?: number;
  }): Promise<Incident> {
    return this.request<Incident>('/incidents', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getIncident(id: string): Promise<Incident> {
    return this.request<Incident>(`/incidents/${id}`);
  }

  async getAllIncidents(page: number = 1, limit: number = 50, status?: string): Promise<PaginatedIncidentsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status) params.append('status', status);
    
    return this.request<PaginatedIncidentsResponse>(`/incidents?${params.toString()}`);
  }

  async updateIncident(id: string, data: Partial<Incident>): Promise<Incident> {
    return this.request<Incident>(`/incidents/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteIncident(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request<{ ok: boolean; deleted: string }>(`/incidents/${id}`, {
      method: 'DELETE',
    });
  }

  async addComment(incidentId: string, content: string): Promise<IncidentComment> {
    return this.request<IncidentComment>(`/incidents/${incidentId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
  }

  async getComments(incidentId: string): Promise<IncidentComment[]> {
    return this.request<IncidentComment[]>(`/incidents/${incidentId}/comments`);
  }

  async linkItem(incidentId: string, itemId: string, itemType: 'note' | 'warning' | 'ban'): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/incidents/${incidentId}/link-item`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_id: itemId, item_type: itemType }),
    });
  }

  async unlinkItem(incidentId: string, itemId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/incidents/${incidentId}/unlink-item?item_id=${itemId}`, {
      method: 'DELETE',
    });
  }

  async getItemsForIncident(incidentId: string): Promise<IncidentLinkedItem[]> {
    return this.request<IncidentLinkedItem[]>(`/incidents/${incidentId}/items`);
  }

  async getIncidentsForItem(itemId: string): Promise<IncidentLinkedItem[]> {
    return this.request<IncidentLinkedItem[]>(`/item/${itemId}/incidents`);
  }

  async linkEvidence(incidentId: string, evidenceId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/incidents/${incidentId}/link-evidence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ evidence_id: evidenceId }),
    });
  }

  async unlinkEvidence(incidentId: string, evidenceId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/incidents/${incidentId}/unlink-evidence?evidence_id=${evidenceId}`, {
      method: 'DELETE',
    });
  }

  async getEvidenceForIncident(incidentId: string): Promise<Evidence[]> {
    return this.request<Evidence[]>(`/incidents/${incidentId}/evidence`);
  }

  async uploadEvidence(incidentId: string, file: File, confidential?: boolean): Promise<Evidence> {
    const formData = new FormData();
    formData.append('file', file);
    if (confidential) formData.append('confidential', 'true');

    const response = await fetch(`${INCIDENTS_API_BASE_URL}/incidents/${incidentId}/evidence/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  async linkUser(incidentId: string, userUuid: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/incidents/${incidentId}/link-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_uuid: userUuid }),
    });
  }

  async unlinkUser(incidentId: string, userUuid: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/incidents/${incidentId}/unlink-user?user_uuid=${userUuid}`, {
      method: 'DELETE',
    });
  }

  async getUsersForIncident(incidentId: string): Promise<{ user_uuid: string; linked_by: string; linked_at: number }[]> {
    return this.request<{ user_uuid: string; linked_by: string; linked_at: number }[]>(`/incidents/${incidentId}/users`);
  }

  async getIncidentsForUser(userUuid: string): Promise<{ incident_id: string; linked_by: string; linked_at: number }[]> {
    return this.request<{ incident_id: string; linked_by: string; linked_at: number }[]>(`/user/${userUuid}/incidents`);
  }

  // ── Incident ↔ Kanban Workspaces (bridge link CRUD) ──────────────────────

  /** `GET /incidents/{id}/kanban-links` — raw bridge rows for an incident. */
  async listKanbanLinks(incidentId: string): Promise<IncidentKanbanLink[]> {
    return this.request<IncidentKanbanLink[]>(`/incidents/${incidentId}/kanban-links`);
  }

  /** `GET /incidents/{id}/workspaces` — denormalised view (link + board + card). */
  async listIncidentWorkspaces(incidentId: string): Promise<IncidentWorkspaceSummary[]> {
    return this.request<IncidentWorkspaceSummary[]>(`/incidents/${incidentId}/workspaces`);
  }

  /** `POST /incidents/{id}/kanban-links` — link an incident to an existing kanban card. */
  async createKanbanLink(incidentId: string, body: CreateIncidentKanbanLinkRequest): Promise<IncidentKanbanLink> {
    return this.request<IncidentKanbanLink>(`/incidents/${incidentId}/kanban-links`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /** `PATCH /incidents/{id}/kanban-links/{cardId}` — update sync flags / notes. */
  async updateKanbanLink(
    incidentId: string,
    cardId: string,
    body: UpdateIncidentKanbanLinkRequest,
  ): Promise<IncidentKanbanLink> {
    return this.request<IncidentKanbanLink>(`/incidents/${incidentId}/kanban-links/${cardId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /** `DELETE /incidents/{id}/kanban-links/{cardId}` — remove the bridge (incident & card kept). */
  async deleteKanbanLink(
    incidentId: string,
    cardId: string,
  ): Promise<{ ok: boolean; deleted: { incident_id: string; card_id: string } }> {
    return this.request<{ ok: boolean; deleted: { incident_id: string; card_id: string } }>(
      `/incidents/${incidentId}/kanban-links/${cardId}`,
      { method: 'DELETE' },
    );
  }

  /**
   * `POST /incidents/{id}/promote-to-card` — create a kanban card on a chosen workspace and
   * bridge it to this incident. Idempotent per `(incidentId, board_id)` pair.
   */
  async promoteToCard(
    incidentId: string,
    body: PromoteIncidentToCardRequest,
  ): Promise<PromoteIncidentToCardResponse> {
    return this.request<PromoteIncidentToCardResponse>(`/incidents/${incidentId}/promote-to-card`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /**
   * `POST /kanban/cards/{cardId}/adopt-as-incident` — create an incident from a card and
   * bridge them. Idempotent if any bridge link already exists for the card.
   */
  async adoptCardAsIncident(
    cardId: string,
    body: AdoptCardAsIncidentRequest,
  ): Promise<AdoptCardAsIncidentResponse> {
    return this.request<AdoptCardAsIncidentResponse>(`/kanban/cards/${cardId}/adopt-as-incident`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // ── Sync-aware incident companions (`*-with-sync`) ───────────────────────

  /** `PATCH /incidents/{id}/with-sync` — same body as legacy `updateIncident`, plus propagation. */
  async updateIncidentWithSync(id: string, body: UpdateIncidentWithSyncRequest): Promise<Incident> {
    return this.request<Incident>(`/incidents/${id}/with-sync`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /** `POST /incidents/{id}/comments/with-sync` — add comment + mirror to linked cards if `sync_comments`. */
  async addCommentWithSync(incidentId: string, content: string): Promise<IncidentComment> {
    return this.request<IncidentComment>(`/incidents/${incidentId}/comments/with-sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
  }

  // ── Incident assignees (staff) ───────────────────────────────────────────

  /** `POST /incidents/{id}/assignees` — assign a staff member. */
  async addAssignee(incidentId: string, userUuid: string): Promise<IncidentAssignee> {
    return this.request<IncidentAssignee>(`/incidents/${incidentId}/assignees`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_uuid: userUuid }),
    });
  }

  /** `POST /incidents/{id}/assignees/with-sync` — assign + propagate if `sync_assignees`. */
  async addAssigneeWithSync(incidentId: string, userUuid: string): Promise<IncidentAssignee> {
    return this.request<IncidentAssignee>(`/incidents/${incidentId}/assignees/with-sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_uuid: userUuid }),
    });
  }

  /** `DELETE /incidents/{id}/assignees/{userUuid}`. */
  async removeAssignee(incidentId: string, userUuid: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/incidents/${incidentId}/assignees/${userUuid}`, {
      method: 'DELETE',
    });
  }

  /** `DELETE /incidents/{id}/assignees/{userUuid}/with-sync`. */
  async removeAssigneeWithSync(incidentId: string, userUuid: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/incidents/${incidentId}/assignees/${userUuid}/with-sync`, {
      method: 'DELETE',
    });
  }

  /** `GET /incidents/{id}/assignees`. */
  async listAssignees(incidentId: string): Promise<IncidentAssignee[]> {
    return this.request<IncidentAssignee[]>(`/incidents/${incidentId}/assignees`);
  }

  /** `GET /user/{userUuid}/assigned-incidents` — incidents this staff member is on. */
  async listAssignedIncidents(userUuid: string): Promise<Incident[]> {
    return this.request<Incident[]>(`/user/${userUuid}/assigned-incidents`);
  }

  // ── Incident watchers ────────────────────────────────────────────────────

  /** `POST /incidents/{id}/watchers` — self-watch (uses identity email server-side). */
  async watchIncident(incidentId: string): Promise<IncidentWatcher> {
    return this.request<IncidentWatcher>(`/incidents/${incidentId}/watchers`, {
      method: 'POST',
    });
  }

  /** `DELETE /incidents/{id}/watchers` — self-unwatch. */
  async unwatchIncident(incidentId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/incidents/${incidentId}/watchers`, {
      method: 'DELETE',
    });
  }

  /** `GET /incidents/{id}/watchers`. */
  async listWatchers(incidentId: string): Promise<IncidentWatcher[]> {
    return this.request<IncidentWatcher[]>(`/incidents/${incidentId}/watchers`);
  }

  // ── Activity & timeline ──────────────────────────────────────────────────

  /** `GET /incidents/{id}/activity?page&limit` — paginated incident activity log. */
  async getIncidentActivity(
    incidentId: string,
    page?: number,
    limit?: number,
  ): Promise<IncidentActivityLog[]> {
    const qs = new URLSearchParams();
    if (page != null) qs.append('page', String(page));
    if (limit != null) qs.append('limit', String(limit));
    const tail = qs.toString();
    return this.request<IncidentActivityLog[]>(
      `/incidents/${incidentId}/activity${tail ? `?${tail}` : ''}`,
    );
  }

  /**
   * `GET /incidents/{id}/timeline?since&limit` — merged timeline of incident comments,
   * incident activity, kanban activity, kanban comments, and kanban move history.
   * Sorted newest first; capped to `limit` (default 200, max 500).
   */
  async getIncidentTimeline(
    incidentId: string,
    params?: { since?: number; limit?: number },
  ): Promise<IncidentTimelineEntry[]> {
    const qs = new URLSearchParams();
    if (params?.since != null) qs.append('since', String(params.since));
    if (params?.limit != null) qs.append('limit', String(params.limit));
    const tail = qs.toString();
    return this.request<IncidentTimelineEntry[]>(
      `/incidents/${incidentId}/timeline${tail ? `?${tail}` : ''}`,
    );
  }

  // ── Augmented list view ──────────────────────────────────────────────────

  /**
   * `GET /incidents-with-kanban-summary` — same incidents as the legacy `/incidents`
   * endpoint, plus five per-incident count columns useful for index pages.
   *
   * NOTE: backend path is `/incidents-with-kanban-summary` (sibling of `/incidents`),
   * deliberately NOT under `/incidents/...` to avoid colliding with `GET /incidents/{id}`.
   *
   * The backend currently returns a bare `IncidentWithKanbanSummary[]` (no pagination
   * envelope). We normalise either shape — bare array OR `{ page, limit, total,
   * total_pages, results }` — into the paginated response so call-sites are stable
   * if/when the backend grows pagination.
   */
  async getIncidentsWithKanbanSummary(
    page: number = 1,
    limit: number = 50,
    status?: string,
  ): Promise<PaginatedIncidentsWithKanbanSummaryResponse> {
    const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) qs.append('status', status);
    const raw = await this.request<unknown>(`/incidents-with-kanban-summary?${qs.toString()}`);

    if (Array.isArray(raw)) {
      const results = raw as IncidentWithKanbanSummary[];
      return {
        page,
        limit,
        total: results.length,
        total_pages: 1,
        results,
      };
    }
    if (raw && typeof raw === 'object') {
      const o = raw as Partial<PaginatedIncidentsWithKanbanSummaryResponse> & {
        results?: unknown;
      };
      const results = Array.isArray(o.results) ? (o.results as IncidentWithKanbanSummary[]) : [];
      return {
        page: typeof o.page === 'number' ? o.page : page,
        limit: typeof o.limit === 'number' ? o.limit : limit,
        total: typeof o.total === 'number' ? o.total : results.length,
        total_pages: typeof o.total_pages === 'number' ? o.total_pages : 1,
        results,
      };
    }
    return { page, limit, total: 0, total_pages: 1, results: [] };
  }
}

export const incidentsApiService = new IncidentsApiService();

